import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button.tsx";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { formatFileSize, loadAnnouncementPdf } from "../../util/announcementPdf.ts";
import styles from "./announcement.module.scss";

interface Props {
  /** Fetched from the server when the viewer opens. */
  announcementId: number;
  fileName: string;
  fileSize: number;
  /** A file the SuperAdmin has chosen but not uploaded yet: shown straight from the browser, nothing is fetched. */
  localFile?: Blob | null;
  /** Tells the dialog to widen while the file is on screen. */
  onViewingChange?: (viewing: boolean) => void;
}

type View =
  | { kind: "closed" }
  | { kind: "loading" }
  | { kind: "open"; url: string; fileName: string }
  | { kind: "error"; message: string };

/**
 * The announcement's PDF, read on the page: a button that opens the file inside the dialog. "Open in new tab" and
 * "Download" are there for a browser whose built-in PDF viewer is switched off or for a phone, which often cannot show a PDF
 * in a frame at all.
 */
export function AnnouncementPdfViewer({ announcementId, fileName, fileSize, localFile, onViewingChange }: Props) {
  const [view, setView] = useState<View>({ kind: "closed" });
  // The blob URL in use, so it is revoked when the viewer closes or goes away.
  const urlRef = useRef<string | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, []);

  const show = useCallback((next: View) => {
    setView(next);
    onViewingChange?.(next.kind === "open" || next.kind === "loading");
  }, [onViewingChange]);

  const handleOpen = useCallback(async () => {
    if (localFile) {
      const url = URL.createObjectURL(localFile);
      urlRef.current = url;
      show({ kind: "open", url, fileName });
      return;
    }
    show({ kind: "loading" });
    const r = await loadAnnouncementPdf(announcementId);
    if ("error" in r) {
      if (aliveRef.current) show({ kind: "error", message: r.error });
      return;
    }
    if (!aliveRef.current) { URL.revokeObjectURL(r.url); return; }
    urlRef.current = r.url;
    show({ kind: "open", url: r.url, fileName: r.fileName });
  }, [announcementId, fileName, localFile, show]);

  const handleHide = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    show({ kind: "closed" });
  }, [show]);

  if (view.kind === "open") {
    return (
      <div className={styles.pdfViewer}>
        <div className={styles.pdfBar}>
          <span className={styles.pdfBarName}>
            <StatusIcon kind="file" size={16} />
            <span className={styles.pdfName}>{view.fileName}</span>
          </span>
          <span className={styles.pdfBarActions}>
            <Button variant="ghost" size="sm" onClick={() => window.open(view.url, "_blank", "noopener")}>Open in new tab</Button>
            <a className={styles.pdfDownload} href={view.url} download={view.fileName}>Download</a>
            <Button variant="outline" size="sm" onClick={handleHide}>Hide PDF</Button>
          </span>
        </div>
        <iframe className={styles.pdfFrame} src={view.url} title={`PDF: ${view.fileName}`} />
      </div>
    );
  }

  return (
    <div>
      <Button variant="outline" onClick={() => void handleOpen()} loading={view.kind === "loading"}>
        <StatusIcon kind="file" size={16} />
        <span>View PDF</span>
        <span className={styles.pdfName}>{fileName}</span>
        <span className={styles.pdfSize}>{formatFileSize(fileSize)}</span>
      </Button>
      {view.kind === "error" && <p className={styles.pdfError} role="alert">{view.message}</p>}
    </div>
  );
}
